import { useEffect } from 'react'
import { isExtensionInstalled, relayEnrichmentCapability } from '../lib/extension'
import { mintExtensionEnrichmentCapability } from '../lib/teamEnrichment'

const REFRESH_BEFORE_EXPIRY_MS = 5 * 60_000
const FALLBACK_REFRESH_MS = 30 * 60_000

interface Props {
  teamId: string | null
}

export default function ExtensionEnrichmentCapabilityRelay({ teamId }: Props) {
  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const clearTimer = () => {
      if (timer) clearTimeout(timer)
      timer = null
    }

    const relay = async () => {
      clearTimer()
      if (!teamId) {
        relayEnrichmentCapability(null)
        return
      }
      if (!isExtensionInstalled()) {
        timer = setTimeout(relay, 5000)
        return
      }

      try {
        const result = await mintExtensionEnrichmentCapability(teamId, 'harmonic')
        if (cancelled) return

        if (result.token) {
          relayEnrichmentCapability(result)
          const delay = Math.max(60_000, result.expiresAt - Date.now() - REFRESH_BEFORE_EXPIRY_MS)
          timer = setTimeout(relay, delay)
        } else {
          relayEnrichmentCapability(null)
          timer = setTimeout(relay, FALLBACK_REFRESH_MS)
        }
      } catch {
        if (cancelled) return
        // Keep any still-valid capability already stored by the extension.
        // A transient Edge Function/network failure while the user opens Slash
        // should not break an add-in session that is otherwise valid.
        timer = setTimeout(relay, FALLBACK_REFRESH_MS)
      }
    }

    relay()
    window.addEventListener('fwdslash:team-enrichment-changed', relay)

    return () => {
      cancelled = true
      window.removeEventListener('fwdslash:team-enrichment-changed', relay)
      clearTimer()
    }
  }, [teamId])

  return null
}
